/**
 * useAntdTreeA11y
 *
 * Patches antd `<Tree>` instances with ARIA attributes that keep in sync as
 * the tree is checked, expanded, or selected:
 *
 *  Container:  role="tree"
 *  Group node: role="group"  (child list inside an expanded node)
 *  Each node:  role="treeitem"
 *              aria-expanded  (true | false | undefined for leaves)
 *              aria-selected  (true | false)
 *              aria-checked   (true | false | "mixed" for half-checked)
 *              aria-disabled  (true | false)
 *              aria-label     ("Select <title text>")
 *              tabIndex       (0 for focused node, -1 for others)
 *
 * A single MutationObserver handles all provided prefixes.
 *
 * @param prefixes  antd CSS prefixes to patch. Defaults to both `"ant"` (v4)
 *                  and `"ant-v5"` (v5 with prefixCls="ant-v5").
 *
 * Usage: call once inside ConfigurationProvider.
 *   useAntdTreeA11y(); // patches both .ant-tree and .ant-v5-tree
 */
import { useEffect } from 'react';

const DEFAULT_PREFIXES = ['ant', 'ant-v5'];

function buildClasses(prefix: string) {
  return {
    tree: `${prefix}-tree`,
    treeList: `${prefix}-tree-list`,
    treenode: `${prefix}-tree-treenode`,
    checkbox: `${prefix}-tree-checkbox`,
    checkboxChecked: `${prefix}-tree-checkbox-checked`,
    checkboxIndeterminate: `${prefix}-tree-checkbox-indeterminate`,
    checkboxDisabled: `${prefix}-tree-checkbox-disabled`,
    switcher: `${prefix}-tree-switcher`,
    switcherNoop: `${prefix}-tree-switcher-noop`,
    switcherOpen: `${prefix}-tree-switcher_open`,
    contentWrapper: `${prefix}-tree-node-content-wrapper`,
    title: `${prefix}-tree-title`,
    disabled: `${prefix}-tree-treenode-disabled`,
    selected: `${prefix}-tree-treenode-selected`,
    childTree: `${prefix}-tree-child-tree`,
  };
}

type Classes = ReturnType<typeof buildClasses>;

function patchTree(tree: Element, c: Classes): void {
  // Container
  // Matching a11y structure of antdv6 tree
  tree
    .querySelectorAll<HTMLElement>(`[role="tree"]:not(.${c.treeList})`)
    .forEach((t) => {
      t.removeAttribute('role');
    });

  tree.querySelectorAll<HTMLElement>(`.${c.treeList}`).forEach((treeList) => {
    treeList.setAttribute('role', 'tree');
    if (!treeList.getAttribute('aria-label')) {
      treeList.setAttribute('aria-label', 'Tree');
    }
  });

  // Child group wrappers
  tree.querySelectorAll<HTMLElement>(`.${c.childTree}`).forEach((group) => {
    group.setAttribute('role', 'group');
  });

  // Each node
  const treenodes = tree.querySelectorAll<HTMLElement>(`.${c.treenode}`);
  treenodes.forEach((node) => {
    node.setAttribute('role', 'treeitem');

    // aria-expanded
    const switcher = node.querySelector<HTMLElement>(`.${c.switcher}`);
    const isLeaf = switcher?.classList.contains(c.switcherNoop) ?? true;
    if (isLeaf) {
      node.removeAttribute('aria-expanded');
    } else {
      node.setAttribute(
        'aria-expanded',
        String(switcher?.classList.contains(c.switcherOpen) ?? false),
      );
    }

    // aria-selected
    node.setAttribute(
      'aria-selected',
      String(node.classList.contains(c.selected)),
    );

    // aria-checked / aria-disabled (checkbox trees)
    const checkbox = node.querySelector<HTMLElement>(`.${c.checkbox}`);
    if (checkbox) {
      checkbox.setAttribute('role', 'checkbox');
      const isIndeterminate = checkbox.classList.contains(
        c.checkboxIndeterminate,
      );
      const isChecked = checkbox.classList.contains(c.checkboxChecked);
      const isCheckboxDisabled = checkbox.classList.contains(
        c.checkboxDisabled,
      );
      node.setAttribute(
        'aria-checked',
        isIndeterminate ? 'mixed' : String(isChecked),
      );
      checkbox.setAttribute(
        'aria-checked',
        isIndeterminate ? 'mixed' : String(isChecked),
      );
      if (isCheckboxDisabled) {
        node.setAttribute('aria-disabled', 'true');
        checkbox.setAttribute('aria-disabled', 'true');
      } else {
        node.removeAttribute('aria-disabled');
        checkbox.removeAttribute('aria-disabled');
      }
    }

    // aria-disabled (non-checkbox disabled nodes)
    if (node.classList.contains(c.disabled)) {
      node.setAttribute('aria-disabled', 'true');
    }

    // aria-label on the content wrapper
    const contentWrapper = node.querySelector<HTMLElement>(
      `.${c.contentWrapper}`,
    );
    if (contentWrapper) {
      const titleText =
        node.querySelector(`.${c.title}`)?.textContent?.trim() ?? 'tree node';
      contentWrapper.setAttribute('aria-label', `Select ${titleText}`);
    }

    // tabIndex (roving tabindex — only set if not already present)
    if (!node.hasAttribute('tabindex')) {
      node.setAttribute('tabindex', '-1');
    }
  });
}

// ─── hook ─────────────────────────────────────────────────────────────────────

export default function useAntdTreeA11y(
  prefixes: string[] = DEFAULT_PREFIXES,
): void {
  // Derive a stable primitive so useEffect has a proper dependency — the
  // effect re-runs only when the prefix list actually changes.
  const prefixKey = prefixes.join(',');

  useEffect(() => {
    const classesPerPrefix = prefixes.map(buildClasses);

    // All tree root selectors joined — used to quickly check if a mutation
    // is inside any of the watched tree types.
    const treeSelectors = classesPerPrefix.map((c) => `.${c.tree}`).join(', ');

    const patchAllTrees = () => {
      classesPerPrefix.forEach((cls) => {
        document
          .querySelectorAll<HTMLElement>(`.${cls.tree}`)
          .forEach((tree) => {
            patchTree(tree, cls);
          });
      });
    };

    // Initial patch
    patchAllTrees();

    if (!window.MutationObserver) {
      return;
    }

    const observer = new window.MutationObserver((mutations) => {
      const affectsTree = mutations.some((m) => {
        const target = m.target as Element;
        return (
          target.matches?.(treeSelectors) ||
          target.closest?.(treeSelectors) !== null ||
          Array.from(m.addedNodes).some(
            (n) =>
              (n as Element).matches?.(treeSelectors) ||
              (n as Element).querySelector?.(treeSelectors),
          )
        );
      });

      if (affectsTree) {
        patchAllTrees();
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      // Only class changes matter (expand/check/select update CSS classes)
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, [prefixKey]);
}
